import {
  Avatar,
  Box,
  Checkbox,
  Flex,
  Grid,
  GridItem,
  Highlight,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback } from 'react';
import { SavedList } from '~/clientApi/savedLists.model';
import { CategoryTag } from '~/components/CategoryTag';
import { GenderTag } from '~/components/GenderTag';
import { HighlightSearchTerm } from '~/components/HighlightSearchTerm';
import WebLinks from '~/components/WebLinks';
import { routes } from '~/core/router';
import { getMainPlatformData } from '~/core/utils';
import { useGetCreatorLocation } from '~/hooks/useGetCreatorLocation';
import { useListsStore } from '~/screens/WhalarModeListDetailScreen/store/useSavedListStore';
import { CreatorButtons } from '~/screens/WhalarModeScreen/components/StackView/CreatorInfo/CreatorButtons';
import { Platforms } from '~/screens/WhalarModeScreen/components/Table/Platforms';
import { useMarketerSearchTalentStore } from '~/screens/store/useMarketerSearchTalentStore';
import { ICreator } from '~/types';

import { stackNameLinkStyle } from '../../styles';
import { ShareCreatorButton } from './ShareCreatorButton';
import { ViewProfileButton } from './ViewProfileButton';

interface ICreatorInfoDesktop {
  list?: SavedList;
  creator: ICreator;
  note?: string;
  isProfile?: boolean;
  isShared?: boolean;
  onCheck?: (isChecked: boolean) => void;
  onNoteOpen?: () => void;
}

export const CreatorInfoDesktop: React.FC<ICreatorInfoDesktop> = ({
  list,
  creator,
  isProfile,
  isShared,
  onCheck,
  onNoteOpen,
  note,
}) => {
  const { age, bioAgency, slug, webLinks } = creator;
  const { getLocation } = useGetCreatorLocation();
  const creatorLocation = getLocation(creator);
  const { avatar, name = '' } = getMainPlatformData(creator);

  const { creators } = useListsStore();
  const { query } = useMarketerSearchTalentStore();
  const isSavedListsEnabled = true;
  const router = useRouter();
  const isGlobalSearchPage = router.pathname === routes.globalSearch;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onCheck?.(event.target.checked);
    },
    [onCheck],
  );

  return (
    <Grid
      templateColumns="repeat(4, 1fr)"
      display={{ base: 'none', lg: 'grid' }}
    >
      <GridItem rowSpan={2} colSpan={1} colEnd={1}>
        <Avatar
          width={32}
          height={32}
          marginRight={6}
          borderRadius="lg"
          src={avatar}
        />
      </GridItem>
      <GridItem colSpan={2}>
        <Flex>
          <Flex flexDirection="column" aria-label="Creator Info">
            <NextLink
              href={
                isShared
                  ? `https://${creator.slug}.${process.env.NEXT_PUBLIC_SHARED_PROFILES_HOST}`
                  : routes.profileOverview(creator.slug || '')
              }
            >
              <Box style={{ textDecoration: 'none', cursor: 'pointer' }}>
                <Text
                  fontSize="32px"
                  fontWeight="500"
                  color="primary.700"
                  whiteSpace="nowrap"
                  {...(!isProfile && stackNameLinkStyle)}
                >
                  {isGlobalSearchPage ? (
                    <HighlightSearchTerm>{name}</HighlightSearchTerm>
                  ) : (
                    <Highlight
                      query={query}
                      styles={{ color: 'secondary.500' }}
                    >
                      {name}
                    </Highlight>
                  )}
                </Text>
              </Box>
            </NextLink>

            <Flex alignItems="center" gap={1}>
              {creatorLocation ? (
                <>
                  <Text fontWeight="400" fontSize="md" color="primary.700">
                    <HighlightSearchTerm>{creatorLocation}</HighlightSearchTerm>
                  </Text>
                </>
              ) : null}
              {age ? (
                <Text
                  fontWeight="400"
                  fontSize="md"
                  marginLeft={1}
                  color="primary.700"
                >
                  {age} years old
                </Text>
              ) : null}
            </Flex>
          </Flex>

          <Flex flexDirection="column">
            <Flex
              gap={3}
              alignItems="flex-start"
              marginLeft={10}
              aria-label="tags"
              marginTop={6}
            >
              <CategoryTag creator={creator} marginRight={4} />
              <GenderTag creator={creator} marginRight={6} />
            </Flex>
          </Flex>
        </Flex>
      </GridItem>

      <GridItem colSpan={2}>
        <Flex
          flexDirection="row"
          gap={4}
          justifyContent="flex-end"
          marginRight={6}
        >
          <CreatorButtons
            creator={creator}
            list={list}
            isShared={isShared}
            onNoteOpen={onNoteOpen}
            note={note}
          />
          {!isShared && slug && <ViewProfileButton slug={creator.slug} />}
          {slug && !isShared && isProfile && creator ? (
            <ShareCreatorButton creator={creator} />
          ) : null}
          {!list?.name.match(/Roster$/) &&
            onCheck &&
            !isProfile &&
            !isShared &&
            isSavedListsEnabled && (
              <Flex onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  isChecked={!!creators.find((c) => c.id === creator.id)}
                  marginLeft={2}
                  onChange={handleChange}
                />
              </Flex>
            )}
        </Flex>
      </GridItem>
      <GridItem colSpan={4}>
        <Flex flexDirection="column">
          <Flex gap={10}>
            <Flex alignItems="center" gap={1} paddingTop={0}>
              <Box minWidth={40}>
                <Platforms
                  platforms={creator.platforms}
                  isOffPlatform={creator.isOffPlatform}
                  flexDirection="column"
                  marginTop={1}
                  view="stacked"
                />
                <Box marginTop={3}>
                  <WebLinks links={webLinks} />
                </Box>
              </Box>
            </Flex>
            {bioAgency && (
              <Flex justifyContent="center" marginBottom={2}>
                <Text fontSize="md" lineHeight={4}>
                  <HighlightSearchTerm>{bioAgency}</HighlightSearchTerm>
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </GridItem>
    </Grid>
  );
};
